import { LifeBuoy, Shield, Clock, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function BCPPage() {
  const { sectionColor } = useNavigation();
  
  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="business-continuity-planning"
        icon={LifeBuoy}
        title="Business Continuity Planning Coming Soon"
        description="Comprehensive BCP/DR planning with RTO/RPO tracking, recovery strategies, testing workflows, and plan activation will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Business Continuity Planning</h1>
          <p className="text-neutral-400 mt-1">
            Manage BCP/DR plans and recovery objectives
          </p>
        </div>
        <button className="btn-primary">
          Create BCP Plan
        </button>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Active Plans</p>
              <p className="text-2xl font-bold text-white mt-1">18</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <LifeBuoy className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Critical Processes</p>
              <p className="text-2xl font-bold text-white mt-1">47</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Shield className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Avg RTO</p>
              <p className="text-2xl font-bold text-success-500 mt-1">4h</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <Clock className="text-success-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Tests Passed</p>
              <p className="text-2xl font-bold text-success-500 mt-1">16/18</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <CheckCircle className="text-success-500" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* BCP Plans Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Business Continuity Plans</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Plan</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Critical Process</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">RTO</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">RPO</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Last Test</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Payment Processing DR</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Online Payments</td>
                <td className="py-3 px-4 text-sm text-success-400">2h</td>
                <td className="py-3 px-4 text-sm text-success-400">15min</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-15</td>
                <td className="py-3 px-4"><span className="badge-success">Passed</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Data Center Failover</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Core Systems</td>
                <td className="py-3 px-4 text-sm text-success-400">4h</td>
                <td className="py-3 px-4 text-sm text-success-400">1h</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-09-28</td>
                <td className="py-3 px-4"><span className="badge-warning">Test Due</span></td>
              </tr>
              <tr className="hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Customer Support Recovery</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Support Operations</td>
                <td className="py-3 px-4 text-sm text-warning-400">8h</td>
                <td className="py-3 px-4 text-sm text-warning-400">4h</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-20</td>
                <td className="py-3 px-4"><span className="badge-success">Passed</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
